import { alpha, type Theme } from '@mui/material/styles';

/**
 * The report designer's own look. Its toolbar, its inspector and the marks on a card are the
 * POI type designer's (`PoiDesigner/designer.styles.ts`), so the two editors read as one
 * family; what is here is the three-column layout and the sections list, which that one
 * does not have. The canvas has a file of its own (`canvas/DesignCanvas.styles.ts`).
 */

/** How tall a column is where it keeps to the screen: the screen, less the room around the editor. */
const SCREEN = 'calc(100vh - 32px)';

/**
 * Three columns where there is room for them: the palette and the sections, the page, the
 * settings of what is selected. Narrower than that, the settings go under the left column
 * and the page keeps the rest of the row. Narrower still, the three stand one under the
 * other, the page in the middle: it cannot be composed on in a narrow column.
 *
 * The page is what is worked on, and beside the application's own menu a laptop's screen
 * leaves it little: up to the widest breakpoint the two columns beside it are narrower and
 * stand closer, which is a third more sheet at 1366 pixels.
 */
export const layoutStyles = {
  display: 'grid',
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    md: '260px minmax(0, 1fr)',
    lg: '216px minmax(0, 1fr) 280px',
    xl: '260px minmax(0, 1fr) 320px',
  },
  gridTemplateAreas: {
    xs: '"left" "page" "settings"',
    md: '"left page" "settings page"',
    lg: '"left page settings"',
  },
  // In two columns the settings follow the left column at once, whatever the page's height.
  gridTemplateRows: { md: 'auto 1fr', lg: 'auto' },
  gap: { xs: 2, lg: 1.5, xl: 2 },
  alignItems: 'start',
  p: { xs: 2, lg: 1.5, xl: 2 },
};

/**
 * The palette over the sections list. Beside the page it keeps to the screen and scrolls by
 * itself, so that the palette is still in reach when the list below it is long.
 */
export const leftCellStyles = {
  gridArea: 'left',
  minWidth: 0,
  position: { lg: 'sticky' },
  top: { lg: 16 },
  maxHeight: { lg: SCREEN },
  overflowY: { lg: 'auto' },
};

/**
 * The page: the canvas or the PDF, under the switch between them. It keeps to the screen
 * while the columns beside it scroll with the page, and scrolls its sheets within itself.
 */
export const pageCellStyles = {
  gridArea: 'page',
  display: 'flex',
  flexDirection: 'column',
  gap: 1,
  minWidth: 0,
  position: { md: 'sticky' },
  top: { md: 16 },
  height: { xs: 640, md: SCREEN },
};

/** The switch between the two views of the page, and what is said beside it. */
export const viewBarStyles = {
  alignItems: 'center',
  gap: 1.5,
  flexWrap: 'wrap',
};

export const viewBodyStyles = {
  flex: 1,
  minHeight: 0,
};

/** The PDF preview inside that body: it stays there while the canvas is the open view, out of sight. */
export const viewStyles = { height: '100%' };

export const viewHiddenStyles = { display: 'none' };

/**
 * The cell of a PDF preview that stands beside a form, as on the report branding page: it
 * keeps to the screen while the form beside it scrolls with the page.
 */
export const previewCellStyles = {
  gridColumn: { xs: 'auto', md: '1 / -1', lg: 'auto' },
  position: { lg: 'sticky' },
  top: { lg: 16 },
  height: { xs: 640, lg: SCREEN },
  minWidth: 0,
};

/** The inspector, in its place in the layout. Beside the page it keeps to the screen as the POI designer's does. */
export const settingsCellStyles = {
  gridArea: 'settings',
  position: { lg: 'sticky' },
  top: { lg: 16 },
  maxHeight: { lg: SCREEN },
  overflowY: 'auto',
};

/** A line under the toolbar: that the design is read-only, that it changed elsewhere, or that it is gone. */
export const bannerStyles = {
  px: 2,
  py: 1.25,
  gap: 1.5,
  alignItems: 'center',
  borderBottom: '1px solid',
  borderColor: 'divider',
  color: 'text.secondary',
  fontSize: 14,
  backgroundColor: (theme: Theme) => alpha(theme.palette.common.black, 0.16),
  '& i': { fontSize: 18 },
};

export const conflictBannerStyles = {
  ...bannerStyles,
  color: 'warning.light',
  backgroundColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
};

/** What stands in the way of a save, a line each. A line is a button to the place it is about. */
export const issueListStyles = {
  px: 2,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
};

export const issueLineStyles = {
  display: 'block',
  width: '100%',
  py: 0.25,
  textAlign: 'left',
  fontSize: 13,
  color: 'warning.light',
  '&:hover': { textDecoration: 'underline' },
};

/** One part of the design in the sections list: the theme, the summary, a page. */
export const entryStyles = {
  width: '100%',
  px: 1.25,
  py: 0.875,
  gap: 1.25,
  alignItems: 'center',
  justifyContent: 'flex-start',
  textAlign: 'left',
  borderRadius: 1.5,
  transition: 'background-color 120ms ease',
  '& i': { fontSize: 18, color: 'text.secondary' },
  '&:hover': { backgroundColor: 'action.hover' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.light', outlineOffset: -2 },
};

/** Selected: the POI designer's tint and thin accent edge. */
export const entrySelectedStyles = {
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
  boxShadow: (theme: Theme) => `inset 2px 0 0 ${theme.palette.primary.main}`,
  '&:hover': { backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12) },
};

/** The covers and the closings: a heading over their pages. */
export const groupTitleStyles = {
  mt: 1.5,
  mb: 0.5,
  px: 1.25,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  color: 'text.secondary',
};

/** Something an issue is about. */
export const issueDotStyles = {
  width: 7,
  height: 7,
  flexShrink: 0,
  borderRadius: '50%',
  backgroundColor: 'warning.main',
};

/**
 * What the inspector is showing the settings of. Not in capitals: a style that upper-cases
 * goes by the page's language, which is the interface's, and a title here can be text an
 * author wrote in another one.
 */
export const inspectorTitleStyles = {
  fontSize: 15,
  fontWeight: 700,
};

/** One row of a list inside the inspector: a value of a `fields` block, a signature line. */
export const listRowStyles = {
  p: 1.25,
  gap: 1,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
};

/** A status of the checkpoints filter: on or off, like a chip. */
export const statusChipStyles = {
  px: 1.25,
  py: 0.375,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  fontSize: 13,
  color: 'text.secondary',
  '&:hover': { backgroundColor: 'action.hover' },
  '&.Mui-disabled': { opacity: 0.5 },
};

export const statusChipActiveStyles = {
  color: 'primary.light',
  borderColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.55),
  backgroundColor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12),
};
